const API_BASE_URL = process.env.REACT_APP_API_URL;
const API_KEY = process.env.REACT_APP_API_KEY;

class ControlledHttpError extends Error {
  constructor(message, status, payload) {
    super(message);
    this.name = 'ControlledHttpError';
    this.status = status;
    this.payload = payload;
  }
}

const normalizeEmail = (value = "") =>
  String(value || "").trim().toLowerCase();

const notifyOutage = (response) => {
  const retryAfter = response.headers.get('Retry-After');
  const secs = retryAfter ? (isNaN(Number(retryAfter)) ? Math.max(0, Math.ceil((Date.parse(retryAfter) - Date.now()) / 1000)) : Number(retryAfter)) : 30;
  const until = Date.now() + secs * 1000;
  const reason = response.status === 429 ? 'rate-limit' : 'service-unavailable';
  window.dispatchEvent(new CustomEvent('app:outage', { detail: { until, reason } }));
};

class AuthService {
  constructor() {
    this.currentUser = null;
    this.authEpoch = 0;
    this.__inFlight = new Map();
    this.__refreshPromise = null;
  }

  setCurrentUser(user) {
    this.currentUser = user || null;
  }

  getCurrentUser() {
    return this.currentUser;
  }

  beginNewAuthSession() {
    this.authEpoch += 1;
    this.__refreshPromise = null;
    this.__inFlight.clear();
    return this.authEpoch;
  }

  clearStoredAuthData() {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenDuration');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('currentRole');
    localStorage.removeItem('activeRole');
    localStorage.setItem('auth:warning', '0');
  }

  // Hacer login
  // src/services/authService.js
  async login(email, password) {
    const loginEpoch = this.beginNewAuthSession();
    this.clearStoredAuthData();
    const cleanEmail = normalizeEmail(email);

    const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": API_KEY,
      },
      body: JSON.stringify({
        email: cleanEmail,
        password,
      }),
    });

    const text = await response.text();
    let data = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = null;
    }

    if (!response.ok) {
      throw new Error(data?.error || data?.message || "Error en login");
    }

    if (loginEpoch !== this.authEpoch) {
      const error = new Error("Inicio de sesión cancelado");
      error.status = 409;
      error.isAuthError = true;
      throw error;
    }

    if (data?.refreshToken) {
      localStorage.setItem("refreshToken", data.refreshToken);
    }

    return data;
  }

  // authService.js
  async verifyToken() {
    const token = localStorage.getItem('token');
    if (!token) {
      const err = new Error('No hay token');
      err.status = 401;
      throw err;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-api-key': API_KEY,
        },
      });

      const text = await response.text();
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = null;
      }

      if (!response.ok) {
        console.error('verifyToken fallo:', response.status, data);

        const msg =
          response.status === 401
            ? 'Token inválido'
            : 'Error al verificar token';

        const error = new Error(msg);
        error.status = response.status;
        error.payload = data;
        throw error;
      }

      if (!data || !data.usuario) {
        const error = new Error('Respuesta inválida del servidor');
        error.status = 500;
        error.payload = data;
        throw error;
      }

      this.setCurrentUser(data.usuario);
      // ✅ devolvemos SOLO el usuario
      return data.usuario;

    } catch (error) {
      console.error('Error verificando token:', error);
      throw error;
    }
  }


  // Renovar token usando refresh token
  async refreshToken() {
    const refreshEpoch = this.authEpoch;
    const refreshToken = localStorage.getItem('refreshToken');

    if (!refreshToken) {
      const error = new Error('No hay refresh token disponible');
      error.status = 401;
      error.isAuthError = true;
      throw error;
    }

    try {

      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken })
      });


      if (!response.ok) {
        const error = new Error(`Error renovando token: ${response.status} ${response.statusText}`);
        error.status = response.status;

        if (response.status === 401) {
          error.isAuthError = true;
          localStorage.removeItem('refreshToken');
        }

        throw error;
      }

      const data = await response.json();

      if (refreshEpoch !== this.authEpoch) {
        const error = new Error('Refresh token descartado por cambio de sesión');
        error.status = 409;
        throw error;
      }

      // Actualizar access token
      localStorage.setItem('token', data.token);

      // Actualizar refresh token si viene uno nuevo
      if (data.refreshToken) {
        localStorage.setItem('refreshToken', data.refreshToken);
      }

      return data;
    } catch (error) {
      console.error('Error renovando token:', error);

      if (error.status === 401 || error.isAuthError) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
      }

      throw error;
    }
  }


  // Hacer peticiones autenticadas
  async authenticatedRequest(url, options = {}) {
    const inFlight = this.__inFlight;
    const sleep = (ms) => new Promise(r => setTimeout(r, ms));

    const safeJson = async (resp) => {
      try { return await resp.clone().json(); } catch { return null; }
    };

    const parseRetryAfter = (h) => {
      if (!h) return null;
      const n = Number(h);
      if (!Number.isNaN(n)) return n * 1000;
      const ts = Date.parse(h);
      return Number.isNaN(ts) ? null : Math.max(0, ts - Date.now());
    };

    const bodyToKey = (b) => {
      if (!b) return "";
      if (typeof FormData !== "undefined" && b instanceof FormData) return "[[FormData]]";
      if (typeof b === "string") return b;
      try { return JSON.stringify(b); } catch { return "[[UnserializableBody]]"; }
    };

    const token = localStorage.getItem('token');
    const finalUrl = /^https?:\/\//i.test(url) ? url : `${API_BASE_URL}${url}`;

    const activeRole = localStorage.getItem("activeRole") || "";

    const headers = {
      'x-api-key': API_KEY,
      'x-active-role': activeRole,   // 👈 AÑADIDO
      ...(options.headers || {}),
    };

    const config = { ...options, headers };
    const isFormData = typeof FormData !== 'undefined' && (options.body instanceof FormData);

    if (!isFormData && !headers['Content-Type'] && config.body != null) {
      headers['Content-Type'] = 'application/json';
    }
    if (token) headers.Authorization = `Bearer ${token}`;

    const method = (config.method || 'GET').toUpperCase();

    const noRetry =
      options?.noRetry === true ||
      options?.skipRetry === true ||
      options?.retry === false ||
      Number(options?.retries) === 0 ||
      Number(options?.maxRetries) === 0;

    const dedupeEnabled = options.dedupe !== false;
    const key = `${method} ${finalUrl} :: ${bodyToKey(config.body)}`;

    if (dedupeEnabled && inFlight.has(key)) return inFlight.get(key).then(r => r.clone());

    const doFetchWithRetry = async () => {
      const maxRetries = Number.isInteger(options.maxRetries) ? options.maxRetries : 2;
      let attempt = 0;

      while (true) {
        let response = await fetch(finalUrl, config);

        // 401 -> refresh una sola vez, excepto endpoints sensibles donde un 401/400/429
        // puede ser una respuesta funcional y NO debe repetir la acción.
        if (response.status === 401) {
          const skipAuthRefresh =
            options?.skipAuthRefresh === true ||
            options?.noAuthRefresh === true ||
            String(finalUrl).includes("/validate-code");

          if (skipAuthRefresh) {
            return response;
          }

          try {
            if (!this.__refreshPromise) {
              this.__refreshPromise = this.refreshToken().finally(() => {
                this.__refreshPromise = null;
              });
            }

            await this.__refreshPromise;

            const newToken = localStorage.getItem("token");
            const retryHeaders = { ...headers };

            if (newToken) retryHeaders.Authorization = `Bearer ${newToken}`;

            response = await fetch(finalUrl, { ...config, headers: retryHeaders });
          } catch (e) {
            if (e?.status !== 409) {
              localStorage.removeItem("token");
              localStorage.removeItem("refreshToken");
            }
            throw e;
          }
        }

        if (response.status === 429 || response.status === 503) {
          // ✅ Para endpoints sensibles como validar código, NO reintentar.
          if (noRetry) {
            return response;
          }

          // ✅ No reintentar errores controlados del cliente.
          // El 429 de validate-code significa bloqueo del código, no caída del servidor.
          if (response.status === 429 && method !== "GET") {
            return response;
          }

          if (attempt >= maxRetries) {
            notifyOutage(response);
            throw new ControlledHttpError('OUTAGE', response.status, await safeJson(response));
          }

          const ra = parseRetryAfter(response.headers.get('Retry-After'));
          const base = ra != null ? ra : (500 * Math.pow(2, attempt));
          const jitter = Math.floor(Math.random() * 300);
          await sleep(base + jitter);
          attempt++;
          continue;
        }

        return response;
      }
    };

    try {
      const p = doFetchWithRetry();
      if (dedupeEnabled) inFlight.set(key, p);
      return await p;
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      if (error instanceof ControlledHttpError && (error.status === 429 || error.status === 503)) throw error;

      if (process.env.NODE_ENV !== 'production') {
        console.error('Error en petición autenticada:', error);
      }
      throw error;
    } finally {
      if (dedupeEnabled) inFlight.delete(key);
    }
  }

  // 🆕 NUEVO: Subir foto de identificación (onboarding)
  async uploadAuthPhoto(blob, filename = "auth.jpg") {
    if (!blob) {
      const e = new Error("No hay imagen para subir");
      e.status = 400;
      throw e;
    }

    const form = new FormData();
    form.append("photo", blob, filename);

    const response = await this.authenticatedRequest("/api/auth/photo", {
      method: "POST",
      body: form,
      dedupe: false,
    });

    const text = await response.text();
    let data = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = null;
    }

    if (!response.ok) {
      const err = new Error(data?.error || "Error subiendo foto");
      err.status = response.status;
      err.payload = data;
      throw err;
    }

    return data;
  }

  async changePassword({ currentPassword, newPassword }) {
    const response = await this.authenticatedRequest("/api/auth/change-password", {
      method: "POST",
      body: JSON.stringify({
        currentPassword,
        newPassword,
      }),
      dedupe: false,
      skipAuthRefresh: true,
    });

    const text = await response.text();
    let data = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = null;
    }

    if (!response.ok) {
      const err = new Error(data?.error || data?.message || "Error cambiando la contraseña");
      err.status = response.status;
      err.payload = data;
      throw err;
    }

    return data || { ok: true };
  }

  // Logout
  logout() {
    this.beginNewAuthSession();
    this.setCurrentUser(null);
    this.clearStoredAuthData();
  }
}

const authService = new AuthService();
export default authService;
